/**
 * WriteSlide Tool — Schema Definitions
 *
 * Zod schemas for editing slides via ReadSlideML search/replace blocks.
 */

import { z } from "zod";
import type { SlideInfoEntry } from "../../../../globals/llm/reminder/types";

// ═══════════════════════════════════════════════════════════════════════════
// INPUT SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

export const SearchReplaceBlockSchema = z.object({
    search: z.string()
        .describe("XML fragment to find (copy EXACTLY from the last readSlide result)"),
    replace: z.string()
        .default("")
        .describe("Modified XML fragment. Empty string = delete the matched content."),
});

export const WriteSlideSchema = z.object({
    uiDescription: z.string()
        .describe('Brief description (max ~15 words) of what this tool call is attempting.'),
    /** Unique identifier for the slide (e.g. "slide_317"). Used for cache lookup. At least one of slideId or slideIndex required. */
    slideId: z.string()
        .optional()
        .describe('Unique identifier for the slide (e.g., "slide_317"). Used for cache lookup. At least one of slideId or slideIndex required.'),
    /** One-based index of the slide in the presentation. Used for COM slide lookup. At least one of slideId or slideIndex required. */
    slideIndex: z.number().int().positive()
        .optional()
        .describe('One-based index of the slide (e.g., 1, 5, 12). At least one of slideId or slideIndex required.'),
    detailLevel: z.enum(["layout", "complete"]).default("complete")
        .describe("Must match the detail level used in the last readSlide call"),
    reReadAfterWrite: z.boolean().default(false)
        .describe("If true, backend re-reads slide and returns updatedXml. If false, skips re-read for faster execution."),
    computeSpatialDelta: z.boolean().default(false)
        .describe("If true, backend computes spatialDelta. Ignored when reReadAfterWrite is false."),
    verbose: z.boolean().default(true)
        .describe("If true, backend emits detailed WriteSlideML logging."),
    changes: z.array(SearchReplaceBlockSchema).min(1)
        .describe("Search/replace blocks. Each search must match exactly once in the last readSlide XML."),
}).refine(
    (data) => data.slideId !== undefined || data.slideIndex !== undefined,
    { message: "At least one of slideId or slideIndex must be provided" }
);

export type WriteSlideParams = z.infer<typeof WriteSlideSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// OUTPUT TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface WriteSlideResult {
    success: boolean;
    message: string;

    /** Complete ReadSlideML XML for the slide after edits */
    updatedXml?: string;

    /** Image ID stored in ContextResolver — queued for next LLM turn */
    screenshotImageId?: string;

    /** Slide tracking entries for pruning (internal, stripped before LLM). */
    _slideInfos?: SlideInfoEntry[];

    /** Operations summary */
    operations?: {
        edited: number;
        created: number;
        deleted: number;
        failed: number;
    };

    /** Per-shape operation details */
    details?: Array<{
        shapeId: string;
        operation: "edit" | "create" | "delete";
        status: "success" | "failed";
        changes?: string[];
        error?: string;
    }>;

    /** Spatial delta (before vs after) */
    spatialDelta?: {
        whitespace?: {
            beforePercent: number;
            afterPercent: number;
        };
        overlaps?: {
            newOverlaps?: Array<{ shapeA: string; shapeB: string }>;
            resolvedOverlaps?: Array<{ shapeA: string; shapeB: string }>;
        };
        offSlide?: {
            newOffSlide?: Array<{ shapeId: string }>;
            resolved?: Array<{ shapeId: string }>;
        };
    };

    /** Metadata operations to execute via Office.js (not COM) */
    metadataOperations?: Array<{
        shapeId: string;
        action: "add" | "remove" | "upsert";
        sourceContextId: string;
        entryType?: string;
        payload?: Record<string, unknown>;
        mapsTo?: Array<{
            text?: string[];
            row?: number;
            col?: number;
        }>;
    }>;

    warnings?: string[];
    errors?: string[];
}
